import {
    useEffect,
    useState
} from "react";

export type ActiveRouteItemType = {
    subButtons?: Array<ActiveRouteItemType>;
    redirectMain?: string;
    redirectSub?: string;
};

export type NavigationStateConstantType = {
    navigation?: NCoreUIKit.Navigation;
};

export type DeepActiveRouteNameConstantType = {
    state?: NCoreUIKit.NavigationState;
};

export type RouteTargetNameConstantType = {
    item: ActiveRouteItemType;
};

export type ActiveRouteConstantType = {
    item: ActiveRouteItemType;
    activeRouteName?: string;
};

export const ACTIVE_ROUTE_EVENTS: Array<string> = [
    "state",
    "focus"
];

export const getNavigationState = ({
    navigation
}: NavigationStateConstantType): NCoreUIKit.NavigationState | undefined => {
    if(!navigation) {
        return undefined;
    }

    if(typeof navigation.getRootState === "function") {
        return navigation.getRootState();
    }

    if(typeof navigation.getState === "function") {
        return navigation.getState();
    }

    return undefined;
};

export const getDeepActiveRouteName = ({
    state
}: DeepActiveRouteNameConstantType): string | undefined => {
    if(!state || !state.routes || !state.routes.length) {
        return undefined;
    }

    const activeRoute = state.routes[state.index ?? 0];

    if(!activeRoute) {
        return undefined;
    }

    if(!activeRoute.state) {
        return activeRoute.name;
    }

    return getDeepActiveRouteName({
        state: activeRoute.state
    }) ?? activeRoute.name;
};

export const getActiveRouteName = ({
    navigation
}: NavigationStateConstantType): string | undefined => {
    return getDeepActiveRouteName({
        state: getNavigationState({
            navigation
        })
    });
};

export const getRouteTargetName = ({
    item
}: RouteTargetNameConstantType): string | undefined => {
    return item.redirectSub ?? item.redirectMain;
};

export const isRouteActive = ({
    activeRouteName,
    item
}: ActiveRouteConstantType): boolean => {
    const targetName = getRouteTargetName({
        item
    });

    return !!targetName && targetName === activeRouteName;
};

export const hasActiveRoute = ({
    activeRouteName,
    item
}: ActiveRouteConstantType): boolean => {
    if(isRouteActive({
        activeRouteName,
        item
    })) {
        return true;
    }

    if(!item.subButtons || !item.subButtons.length) {
        return false;
    }

    return item.subButtons.some((subButton) => hasActiveRoute({
        item: subButton,
        activeRouteName
    }));
};

const useActiveRouteName = ({
    navigation
}: NavigationStateConstantType): string | undefined => {
    const [
        activeRouteName,
        setActiveRouteName
    ] = useState(() => getActiveRouteName({
        navigation
    }));

    useEffect(() => {
        const onNavigationChange = () => {
            setActiveRouteName(getActiveRouteName({
                navigation
            }));
        };

        onNavigationChange();

        if(!navigation || typeof navigation.addListener !== "function") {
            return;
        }

        const unsubscribers = ACTIVE_ROUTE_EVENTS.map((eventName) => navigation.addListener(eventName, onNavigationChange));

        return () => {
            unsubscribers.forEach((unsubscribe) => {
                if(typeof unsubscribe === "function") {
                    unsubscribe();
                }
            });
        };
    }, [
        navigation
    ]);

    return activeRouteName;
};
export default useActiveRouteName;
